import trackingEvents from "./trackingEvents"

const DEBUG = true
let currentTrackingContext = {}

/* Enhanced logging */
const log = (message, data = {}, type = "log") => {
  if (DEBUG) {
    console[type](`[Tracking] ${message}`, data)
  }
}

export const initTrackingContext = (context = {}) => {
  currentTrackingContext = {
    ...currentTrackingContext,
    ...context,
  }
}

/* Active section detection */
const getActiveSection = () => {
  try {
    const activeSelectors = [
      "header a.active",
      ".nav-link.active",
      '[aria-current="page"]',
      '[data-active="true"]',
    ]

    const activeElement = document.querySelector(activeSelectors.join(", "))
    if (activeElement) {
      return activeElement.textContent
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/g, "")
    }

    const { pathname } = window.location
    return pathname.split("/")[1] || "home"
  } catch (error) {
    log("Section detection failed", error, "error")
    return "unknown"
  }
}

/* Element data extraction */
const getElementData = element => {
  const data = {
    element,
    text: (element.textContent?.trim() || "").toLowerCase(),
    href: element.href || element.closest("a")?.href || "",
    page_url: window.location.href,
    page_path: getActiveSection(),
    element_type: element.tagName.toLowerCase(),
    classes: element.className || "",
    attributes: {},
    dataset: {},
  }

  Array.from(element.attributes).forEach(attr => {
    if (attr.name.startsWith("data-")) {
      const key = attr.name.replace("data-", "")
      data.attributes[key] = attr.value
      data.dataset[key] = attr.value
    }
  })

  return data
}

/* Improved Job Tile detection */
const isJobTileLink = elementData => {
  try {
    if (!elementData.href) return false

    const url = new URL(elementData.href)
    return (
      url.hostname.includes("jobs.pga.org") &&
      url.pathname.includes("/detail/") &&
      /^\/detail\/[a-zA-Z0-9]{18}$/.test(url.pathname)
    )
  } catch {
    return false
  }
}

/* Event matching logic */
const matchEventForElement = (element, elementData) => {
  // 1. Check explicit track-event attribute
  if (elementData.attributes["track-event"]) {
    const matchedEvent = trackingEvents.events.find(
      e => e.eventName === elementData.attributes["track-event"],
    )
    if (matchedEvent) return matchedEvent
  }

  // 2. Special cases
  if (isSavePreferencesButton(elementData))
    return getEvent("Save Job Preferences")
  if (isJobTileLink(elementData)) return getEvent("Click Job Tile")
  if (isGetStartedButton(elementData)) return getEvent("Click Get Started")
  if (isSubmitPathToMembershipFormButton(elementData))
    return getEvent("Submit Path To Membership Form")

  // 3. Known patterns
  for (const event of trackingEvents.events) {
    if (matchesEventPattern(element, elementData, event)) {
      return event
    }
  }

  // 4. Default for clickable elements
  if (isClickableElement(element)) return getEvent("Click Link")

  return null
}

/* Helper functions */
const getEvent = eventName => {
  return (
    trackingEvents.events.find(e => e.eventName === eventName) || {
      eventName,
      properties: [
        { name: "page_url" },
        { name: "page_path" },
        { name: "text" },
        { name: "href" },
        { name: "element_type" },
      ],
    }
  )
}

const isSavePreferencesButton = elementData => {
  return (
    elementData.element_type === "button" &&
    (elementData.text.includes("save preferences") ||
      elementData.text.includes("guardar preferencias"))
  )
}

const isGetStartedButton = elementData => {
  return (
    elementData.element_type === "button" &&
    elementData.text.includes("Get Started")
  )
}

const isSubmitPathToMembershipFormButton = elementData => {
  return (
    elementData.element_type === "button" && elementData.text.includes("Submit")
  )
}

const isClickableElement = element => {
  return element.matches('a, button, [role="button"]')
}

/* Pattern matching */
const matchesEventPattern = (element, elementData, event) => {
  const { text, classes } = elementData

  switch (event.eventName) {
    case "Click Nav Menu Item":
      return element.closest("nav, header") !== null

    case "Click Log in":
      return text.includes("log in") || text.includes("sign in")

    case "Click Get Started":
      return text.includes("get started")

    case "Click News Tile":
      return classes.includes("news-tile")

    default:
      return false
  }
}

/* Property collection */
const collectEventProperties = (event, elementData) => {
  const props = {
    page_url: elementData.page_url,
    page_path: elementData.page_path,
    ...currentTrackingContext,
  }

  // Special handling for Job Tile
  if (event.eventName === "Click Job Tile" && elementData.href) {
    try {
      const url = new URL(elementData.href)
      props.job_id = url.pathname.split("/").pop()
    } catch (error) {
      log("Failed to extract job ID", error, "error")
    }
  }

  if (
    event.eventName === "Click Link" ||
    event.eventName === "Click Nav Menu Item"
  ) {
    try {
      props.destination_url = elementData.href
      props.link_text = elementData.text
      if (event.eventName === "Click Link") {
        props.timestamp = new Date().getTime()
        props.referrer = document.referrer
      }
    } catch (error) {
      log("Failed to extract Link data", error, "error")
    }
  }

  // Map other properties
  event.properties.forEach(prop => {
    const value =
      elementData[prop.name] ||
      elementData.attributes[prop.name] ||
      elementData.dataset[prop.name]
    if (value !== undefined) {
      props[prop.name] = value
    }
  })

  return props
}

function trackRolexWidgetClick() {
  const iframe = document.querySelector("iframe")
  if (!iframe || !window.analytics?.track) return

  const parent = iframe.parentElement

  if (window.getComputedStyle(parent).position === "static") {
    parent.style.position = "relative"
  }

  const overlay = document.createElement("div")
  Object.assign(overlay.style, {
    position: "absolute",
    top: `${iframe.offsetTop}px`,
    left: `${iframe.offsetLeft}px`,
    width: `${iframe.offsetWidth}px`,
    height: `${iframe.offsetHeight}px`,
    backgroundColor: "transparent",
    zIndex: "9999",
    cursor: "pointer",
    pointerEvents: "auto",
  })

  overlay.addEventListener("click", () => {
    window.analytics.track("Click Rolex Icon", {
      page_url: window.location.href,
      page_path: getActiveSection(),
    })

    window.open(
      "https://www.rolex.com/?cmpid=dsp_Clock_pga.org/_2021_rlx2101379",
      "_blank",
    )
  })

  parent.appendChild(overlay)
}

/* Click handler */
const handleTrackClick = event => {
  if (event.button !== 0 || event.ctrlKey || event.metaKey) return

  const targetElement = event.target.closest('a, button, [role="button"]')
  if (!targetElement) return

  const newCTAClicks = {
    "report pdr credits": "Click Report PDR Credits",
    "earn credits": "Click Earn Credits",
    "view receipts": "Click View Receipts",
    "view invoices": "Click View Invoices",
  }

  if (targetElement.getAttribute("href") === "/logout") {
    if (sessionStorage.getItem("segment_userId") && window.analytics) {
      sessionStorage.setItem("segment_userId", "anonymous")
      localStorage.setItem("last_identified_traits", JSON.stringify("{}"))
      window.analytics.reset()
      return
    }
  }

  try {
    const elementData = getElementData(targetElement)
    const matchedEvent = matchEventForElement(targetElement, elementData)

    if (!matchedEvent) {
      return
    }
    const properties = collectEventProperties(matchedEvent, elementData)
    delete properties.jobPreference
    delete properties.user
    if (!newCTAClicks[properties.link_text]) delete properties.isLoggedIn
    if (window.analytics?.track) {
      window.analytics.track(
        newCTAClicks[properties.link_text]
          ? newCTAClicks[properties.link_text]
          : matchedEvent.eventName,
        properties,
      )
    }
  } catch (error) {
    log("Tracking error", error, "error")
  }
}

/* Initialize tracking */
export const initTracking = () => {
  if (typeof window === "undefined") return

  document.addEventListener("click", handleTrackClick, {
    capture: true,
    passive: true,
  })
  trackRolexWidgetClick()
}
